import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pencil,
  Type,
  Link2,
  LayoutGrid,
  FileText,
  ImageIcon,
  UploadCloud,
  ShieldCheck,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/domains/organizations";
import { useUpdateBrand } from "../hooks/useUpdateBrand";
import {
  type EditBrandModalProps,
  INDUSTRIES,
} from "@/components/brands/form";

export type { EditBrandModalProps };

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

export const EditBrandModal: React.FC<EditBrandModalProps> = ({
  open,
  setOpen,
  brand,
  onSuccess,
}) => {
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();
  const updateBrandMutation = useUpdateBrand();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [formData, setFormData] = useState({
    name: brand?.name || "",
    description: brand?.description || "",
    industry: brand?.industry || "",
    website_url: brand?.website_url || brand?.websiteUrl || "",
    logo_url: brand?.logo_url || brand?.logoUrl || "",
  });

  const [previewUrl, setPreviewUrl] = useState<string>("");

  useEffect(() => {
    if (brand && open) {
      setFormData({
        name: brand.name || "",
        description: brand.description || "",
        industry: brand.industry || "",
        website_url: brand.website_url || brand.websiteUrl || "",
        logo_url: brand.logo_url || brand.logoUrl || "",
      });
      setLogoFile(null);
    }
  }, [brand, open]);

  useEffect(() => {
    if (logoFile) {
      const objectUrl = URL.createObjectURL(logoFile);
      setPreviewUrl(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else {
      setPreviewUrl(formData.logo_url || "");
    }
  }, [logoFile, formData.logo_url]);

  const industriesList = useMemo(() => {
    if (formData.industry && !INDUSTRIES.includes(formData.industry)) {
      return [formData.industry, ...INDUSTRIES];
    }
    return INDUSTRIES;
  }, [formData.industry]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const processSelectedFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (PNG, JPG, or SVG).",
        variant: "destructive",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast({
        title: "File too large",
        description: "Logo file size must be less than 2MB.",
        variant: "destructive",
      });
      return;
    }

    setLogoFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processSelectedFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processSelectedFile(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setFormData((prev) => ({
      ...prev,
      logo_url: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization?.id || !brand?.id) {
      toast({
        title: "Error",
        description: !currentOrganization?.id
          ? "No organization selected. Please select an organization first."
          : "Invalid brand selected.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: "Required field missing",
        description: "Brand name is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      await updateBrandMutation.mutateAsync({
        id: brand.id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        industry: formData.industry.trim() || undefined,
        website_url: formData.website_url.trim() || undefined,
        logo_url: formData.logo_url.trim() || undefined,
        logo_file: logoFile,
      });

      setOpen(false);
      onSuccess?.();
    } catch {
      // Error handled in useUpdateBrand
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[95vw] max-w-[580px] p-0 rounded-2xl overflow-hidden border-none shadow-2xl bg-white focus:outline-none [&>button:last-child]:hidden">
        {/* Header - Clean white background, no top gradient near 'X' */}
        <div className="p-6 pb-5 flex items-start justify-between border-b border-slate-100 relative bg-white">
          <div className="flex items-start gap-4 pr-10">
            <div className="w-12 h-12 rounded-2xl bg-[#A655F8]/10 flex items-center justify-center text-[#A655F8] shrink-0">
              <Pencil className="w-6 h-6 text-[#A655F8]" />
            </div>

            <div>
              <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                Edit Brand
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500 mt-1 leading-relaxed">
                Update your brand details and keep your profile up to date.
              </DialogDescription>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-lg text-slate-400 focus:outline-none"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="p-6 pt-3 space-y-5 max-h-[calc(85vh-170px)] overflow-y-auto">
            {/* Brand Name */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#A655F8]/10 flex items-center justify-center text-[#A655F8] shrink-0">
                <Type className="w-5 h-5 text-[#A655F8]" />
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <Label htmlFor="edit-brand-name" className="text-sm font-bold text-slate-900 flex items-center gap-1 cursor-pointer">
                  Brand Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit-brand-name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Adidas"
                  className="h-11 rounded-xl border border-slate-200 text-sm text-slate-900 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 focus:border-[#A655F8] focus-visible:border-[#A655F8] transition-colors"
                  required
                />
              </div>
            </div>

            {/* Website */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#A655F8]/10 flex items-center justify-center text-[#A655F8] shrink-0">
                <Link2 className="w-5 h-5 text-[#A655F8]" />
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <Label htmlFor="edit-brand-website" className="text-sm font-bold text-slate-900 cursor-pointer">
                  Website
                </Label>
                <Input
                  id="edit-brand-website"
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => handleInputChange("website_url", e.target.value)}
                  placeholder="https://adidas.com/"
                  className="h-11 rounded-xl border border-slate-200 text-sm text-slate-900 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 focus:border-[#A655F8] focus-visible:border-[#A655F8] transition-colors"
                />
              </div>
            </div>

            {/* Industry */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#A655F8]/10 flex items-center justify-center text-[#A655F8] shrink-0">
                <LayoutGrid className="w-5 h-5 text-[#A655F8]" />
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <Label htmlFor="edit-brand-industry" className="text-sm font-bold text-slate-900 cursor-pointer">
                  Industry
                </Label>
                <Select
                  value={formData.industry}
                  onValueChange={(val) => handleInputChange("industry", val)}
                >
                  <SelectTrigger
                    id="edit-brand-industry"
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 focus:border-[#A655F8] focus-visible:border-[#A655F8] transition-colors"
                  >
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 rounded-xl">
                    {industriesList.map((ind) => (
                      <SelectItem key={ind} value={ind} className="rounded-lg">
                        {ind}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#A655F8]/10 flex items-center justify-center text-[#A655F8] shrink-0">
                <FileText className="w-5 h-5 text-[#A655F8]" />
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <Label htmlFor="edit-brand-description" className="text-sm font-bold text-slate-900 cursor-pointer">
                  Description
                </Label>
                <Textarea
                  id="edit-brand-description"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder="Brief description of the brand"
                  rows={3}
                  className="min-h-[88px] rounded-xl border border-slate-200 text-sm text-slate-900 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 focus:border-[#A655F8] focus-visible:border-[#A655F8] resize-none transition-colors"
                />
              </div>
            </div>

            {/* Brand Logo - Upload area & preview card (no URL link input below) */}
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#A655F8]/10 flex items-center justify-center text-[#A655F8] shrink-0">
                <ImageIcon className="w-5 h-5 text-[#A655F8]" />
              </div>
              <div className="flex-1 space-y-2 min-w-0">
                <Label className="text-sm font-bold text-slate-900">
                  Brand Logo
                </Label>
                <div className="flex items-stretch gap-3">
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn(
                      "flex-1 border-2 border-dashed rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 min-h-[96px]",
                      isDragging
                        ? "border-[#A655F8] bg-[#A655F8]/10 scale-[0.99]"
                        : "border-[#A655F8]/40 hover:border-[#A655F8] bg-[#FAF5FF] hover:bg-[#F3E8FF]/60"
                    )}
                  >
                    <UploadCloud className="w-7 h-7 text-[#A655F8] stroke-[1.75]" />
                    <span className="text-sm font-semibold text-[#A655F8] mt-1">Click to upload</span>
                    <span className="text-[11px] text-slate-500 mt-0.5">PNG, JPG or SVG (Max 2MB)</span>
                    <input
                      ref={fileInputRef}
                      id="edit-brand-logo"
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>

                  {previewUrl && (
                    <div className="relative rounded-2xl bg-white border border-slate-200/90 flex items-center justify-center p-2 w-36 shrink-0 group overflow-hidden self-stretch">
                      <img
                        src={previewUrl}
                        alt="Brand logo preview"
                        className="w-full h-full object-contain"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/95 hover:bg-white border border-slate-200 shadow-xs flex items-center justify-center text-slate-400 hover:text-slate-700 hover:scale-105 transition-all z-10"
                        aria-label="Remove logo"
                      >
                        <X className="w-3.5 h-3.5 stroke-[2.5]" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 px-6 border-t border-slate-100 bg-white flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-slate-500">
              <ShieldCheck className="w-5 h-5 text-[#A655F8] shrink-0" />
              <span className="text-xs leading-tight text-slate-600">
                Your data is secure and will only be used to update this brand.
              </span>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={updateBrandMutation.isPending}
                className="h-10 px-6 rounded-xl border-[#A655F8] bg-white text-[#A655F8] hover:bg-white hover:text-[#A655F8] shadow-none font-medium text-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={updateBrandMutation.isPending || !formData.name.trim()}
                className="h-10 px-6 rounded-xl bg-[#A655F8] hover:bg-[#A655F8] text-white shadow-none font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateBrandMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditBrandModal;
